/**
 * Collection of all users' profiles.  Useful for general-purpose how's-it-going stuff between users.
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/userProfile';

import PagerModel from '@netx/core-app/models/pager';

export default class Users extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			searchUsers() {
				return this.isFiltered() ? 'Filtering users' : 'Loading users';
			},
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			searchUsers() {
				return this.isFiltered() ? 'Filtered users' : 'Loaded users';
			},
		};
	}

	// Everywhere else uses `filterStr` for the, well, filter string
	// just mirror what it would have been onto our search params until
	// we can sort this out in develop
	/** @type {string} */
	get filterStr() {
		if (!this.searchParams) return '';
		return this.searchParams.str;
	}

	/** @type {string} */
	set filterStr(str) {
		if (!this.searchParams) return '';
		return (this.searchParams.str = str);
	}

	/**
	 * @property {object} methods                         - Collection RPC methods
	 * @property {method} methods.read                    - Fetch users
	 * @property {method} methods.downloadUserData        - Download all user data
	 * @property {method} methods._getUsersForCart        - Get users for saved cart
	 * @property {method} methods.getByIds                - Fetch users by passed ids
	 * @property {method} methods.getUserObjectsForGroup  - Fetch users by `groupId`
	 * @property {method} methods.getUsers                - Fetch users by `this.ids()`
	 * @property {method} methods.importUserData          - Import users via CSV/XLS
	 *
	 * @property {method} methods.getUsersLength          - Deprecated - the total user count is on the paging object after a `read`
	 */
	get methods() {
		return {
			/**
			 * Return user objects for users matching search criteria
			 * @param {string} str    Search phrase to filter by
			 * @param {number} level  User type to return, or <code>0</code> for all user types
			 * @param {object} paging Page DTO object
			 * @type {function}
			 */
			read(opts) {
				if (this.groupId != null) {
					return this.getUserObjectsForGroup(this.groupId, opts);
				}
				var searchParams = this.searchParams;
				return [
					'searchUsers',
					searchParams.str,
					searchParams.level,
					this.pager.getPageDTO(),
				];
			},
			// Helpers
			// downloadUserData takes a `UserSelector` object - though it currently only supports all, none and groupIds
			// See: package com.netxposure.module.user.selector.UserSelector for more deails on this object
			// TODO: once this object is fully supported we should mirror it with our own model - then we could pass params and get a valid `UserSelector` to send
			// to any call that will expect one
			downloadUserData: ['downloadUserData', {all: true}],
			_getUsersForCart: ['getUsersForCart', 'cartId'],
			/**
			 * @method
			 * @param {array} ids User ids
			 * @param {object} opts Request options
			 */
			getByIds(ids, opts) {
				if (!_.isArray(ids)) ids = [ids];
				return ['getUserObjects', ids];
			},
			/**
			 * @method
			 * @param {number} groupId Group id
			 * @param {object} opts Request options
			 */
			getUserObjectsForGroup(groupId, opts) {
				return ['getUserObjectsForGroup', groupId, this.pager.getPageDTO()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getUsers(opts) {
				return this.getByIds(this.ids(), opts);
			},
			importUserData: [
				'importUserData',
				'importUserDataParams.fileName',
				'importUserDataParams.option',
			],
			// Deprecated - Check the pager
			getUsersLength: [
				'getUsersLength',
				'searchParams.str',
				'searchParams.level',
				'searchParams.field',
			],
		};
	}

	/**
	 * @property {object}   parsers                 - Collection parsers
	 * @property {function} parsers.getUsers        - Parse the results of our users (fetched by id)
	 * @property {function} parsers.getByIds        - Parse the results of our users (fetched by id)
	 * @property {function} parsers.importUserData  - Parse the results of importing user data
	 */
	get parsers() {
		return {
			read(users, opts = {}) {
				return users.map((user) => ({
					...user,
					password1: null,
					password2: null,
				}));
			},
			/**
			 * @function
			 * @param {array} users Results
			 * @param {object} opts Request options
			 */
			getUsers(users, opts = {}) {
				this.set(
					users.map((user) => ({...user, password1: null, password2: null})),
					opts,
				);
				!opts.silent && this.trigger('sync', this, users, opts);
			},
			/**
			 * @function
			 * @param {array} users Results
			 * @param {array} ids User ids
			 * @param {object} opts Request options
			 */
			getByIds(users, ids, opts = {}) {
				this.set(users, opts);
				!opts.silent && this.trigger('sync', this, users, opts);
			},
			/**
			 * @function
			 * @param {array} results Results
			 * @param {object} opts Request options
			 */
			importUserData(results, opts) {
				// Put back default data
				_.extend(this.importUserDataParams, {
					fileName: '',
					option: 1,
				});
				return results;
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * Param for `getUsersForCart` call
		 * @type {number}
		 * @default
		 */
		this.cartId = opts.cartId;

		/**
		 * Default search params
		 * @memberOf Netx.Collections.Users
		 * @private
		 * @alias defaultSearchParams
		 * @property {object}  searchParams        - The default search params
		 * @property {string}  searchParams.str    - The default search param string for user
		 * @property {number}  searchParams.level  - The default search param number for user level
		 * @default
		 */
		this.defaultSearchParams = {
			str: '',
			level: 0,
		};

		/**
		 * When exporting users you can specify a `group` id
		 * @type {number}
		 * @default
		 */
		this.exportGroupId = opts.exportGroup || 0;

		/**
		 * Fetch users based on `groupId`
		 * @type {number}
		 */
		this.groupId = opts.groupId;

		/**
		 * Import users params
		 * @memberOf Netx.Collections.Users
		 * @alias importUserDataParams
		 * @property {object}  importUserDataParams           - The default search params
		 * @property {string}  importUserDataParams.fileName  - The default import user data file name param
		 * @property {number}  importUserDataParams.option    - The default import user data option param ( 1 - Add only new users, 2 - Only update existing users, 3 - Add and update, 4 - Delete )
		 * @default
		 */
		this.importUserDataParams = _.extend(
			{
				fileName: '',
				option: 1,
			},
			opts.importUserDataParams,
		);

		/**
		 * Paging model
		 * @memberOf Netx.Collections.Users
		 * @alias pager
		 * @type {Netx.Models.Pager}
		 */
		this.pager = new PagerModel(
			_.extend(
				{
					// TODO: collections/models should not need to know about routes/routing
					routesToPage: false,
				},
				opts.pager,
			),
			{collection: this},
		);

		/**
		 * Search params
		 * @memberOf Netx.Collections.Users
		 * @alias searchParams
		 * @property {object}  searchParams        - The default search params
		 * @property {string}  searchParams.str    - The default search param string for user
		 * @property {number}  searchParams.level  - The default search param number for user level
		 * @default
		 */
		this.searchParams = _.extend(
			{},
			this.defaultSearchParams,
			opts.searchParams,
		);

		// Hey, listen!
		this.listenTo(
			this,
			['request:getUsers', 'request:getByIds'].join(' '),
			(collection, xhr, opts) => {
				this.trigger('request:read', this, xhr, opts);
			},
		);
	}

	// TODO: find a way to make this more "agnostic to where it lives" and move to netx-collection
	// If we agree all paging colelctions with search/filter capabilities will utilize this.searchParams
	// clearSearch is all ready to go to Netx.Collection
	/**
	 * Clear search
	 * @method
	 * @param {object} opts Request options
	 */
	clearSearch(opts) {
		// No longer filtered
		this.filtered = false;

		// Make sure we actually need to clear
		const defaults = this.defaultSearchParams;
		if (_.isEqual(defaults, this.searchParams)) {
			return new $.Deferred().resolve();
		}

		// Put back to defaults
		_.extend(this.searchParams, defaults);
		return this.fetch(opts);
	}

	/**
	 * @method
	 * @param {number} id Saved cart id
	 * @param {object} opts Request options
	 */
	getUsersForCart(id, opts) {
		this.cartId = id;
		return this._getUsersForCart(opts);
	}

	/**
	 * Search users
	 * @method
	 * @param {object} searchParams Search params
	 * @param {object} opts Request options
	 */
	search(searchParams, opts) {
		searchParams = searchParams || {};

		// TODO: search and user level do not work together?

		let userLevel = parseInt(searchParams.level);
		// We have to convert this to no access
		if (userLevel === 0) {
			userLevel = -1;
		}
		// Trully a 'no filter'
		else if (isNaN(userLevel)) {
			userLevel = 0;
		}

		// Extend search params
		_.extend(this.searchParams, {
			str: searchParams.str || '',
			level: userLevel,
		});

		// Filtered?
		const defaults = this.defaultSearchParams;
		this.filtered = !_.isEqual(defaults, this.searchParams);

		// Reset paging start
		// Pager fires off a fetch when currentPage changes
		// We are planning on fetching ourselves just below
		// TODO: it does not fire off a fetch becuase we have set routesToPage: false
		// since that is false it will not auto fetch - but silent will prevent the pager
		// view from updating the currently selected page
		// this is all so gross - refactor one day :pray:
		// Basically it goes:
		// pagerModel: page changed, if it does not route to page calls `collection.gotoPage`
		// let our goto page method handle the fetch
		if (this.pager.get('currentPage') !== 1) {
			this.pager.set({currentPage: 1}, opts);
		} else {
			return this.fetch(opts);
		}
	}

	/*
	 * @method
	 * @param {number} page Page number
	 * @param {object} opts Options
	 */
	gotoPage(page, opts) {
		if (page === this.curPage) {
			return new $.Deferred().resolveWith(this);
		}

		this.curPage = parseInt(page);
		return this.fetch(opts);
	}

	/** @type {string} */
	static get __name__() {
		return 'Users';
	}
}
